import React, { useEffect, useState } from 'react';
import api from '../api';

export const MODULE_MAPPING_TYPES = ['text', 'textarea', 'email', 'phone', 'cpf', 'cnpj', 'cep', 'number', 'integer', 'decimal', 'date', 'list'];
export const canMapModuleField = field => field && MODULE_MAPPING_TYPES.includes(field.type) && !field.lockedForStep && !(field.autoFillSource && field.autoFillEditable === false) && !field.readOnly;
const metadataKey = field => `${field.id}__moduleRecord`;

export default function ModuleRecordField({ field, fields = [], values = {}, onChange, disabled = false, className = '', canWriteField = () => true }) {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [retry, setRetry] = useState(0);
  const meta = values[metadataKey(field)];
  const selectedId = meta?.moduleKey === field.linkedModuleKey && meta?.field === field.linkedFieldId ? String(meta.id || '') : '';
  const filterStatus = field.linkedModuleKey === 'customers' ? field.customerFilters?.status || 'ALL' : undefined;
  const filterKind = field.linkedModuleKey === 'customers' ? field.customerFilters?.kind || 'ALL' : undefined;
  const filterState = field.linkedModuleKey === 'customers' ? field.customerFilters?.state || '' : undefined;
  const filterCity = field.linkedModuleKey === 'customers' ? field.customerFilters?.city || '' : undefined;
  useEffect(() => {
    if (disabled || !field.linkedModuleKey || !field.linkedFieldId) return undefined;
    let active = true;
    setLoading(true);
    setError('');
    const timer = setTimeout(async () => {
      try {
        const url = `/forms/module-data/${encodeURIComponent(field.linkedModuleKey)}/options`;
        const params = {
          field: field.linkedFieldId, q: query,
          ...(field.linkedModuleKey === 'customers'
            ? { status: filterStatus, kind: filterKind, state: filterState, city: filterCity }
            : {}),
        };
        const { data } = await api.get(url, { params });
        let rows = Array.isArray(data?.options) ? data.options : [];
        if (selectedId && !rows.some(row => row.id === selectedId)) {
          const selected = await api.get(url, { params: { ...params, id: selectedId } });
          rows = [...rows, ...(selected.data?.options || [])];
        }
        if (active) { setOptions(rows); setHasMore(Boolean(data?.hasMore)); }
      } catch (err) {
        if (active) { setOptions([]); setError(err?.response?.data?.error || 'Não foi possível buscar os registros.'); }
      } finally { if (active) setLoading(false); }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [field.linkedModuleKey, field.linkedFieldId, filterStatus, filterKind, filterState, filterCity, selectedId, query, disabled, retry]);

  if (disabled) return <input id={field.id} value={String(values[field.id] ?? '')} readOnly className={className} />;
  const selectRecord = event => {
    const id = event.target.value;
    const record = options.find(option => option.id === id);
    if (id && !record) return;
    const patch = { [field.id]: record?.value ?? '', [metadataKey(field)]: record ? { id: record.id, moduleKey: field.linkedModuleKey, field: field.linkedFieldId, label: record.label } : null };
    (field.moduleMappings || []).forEach(mapping => {
      const target = fields.find(item => item.id === mapping.target);
      if (mapping.target !== field.id && canMapModuleField(target) && canWriteField(target)) {
        patch[mapping.target] = record?.data?.[mapping.source] ?? '';
      }
    });
    onChange(patch);
  };
  const savedLabel = meta?.label || String(values[field.id] || 'Registro selecionado');
  return <div className="space-y-2">
    <input type="search" aria-label={`Buscar em ${field.label || field.id}`} placeholder={field.linkedModuleKey === 'customers' ? "Buscar cliente por nome, documento ou contato..." : "Digite nome, e-mail ou código para buscar..."} value={query} onChange={event => setQuery(event.target.value)} className={className} />
    <select id={field.id} aria-label={field.label || field.id} value={selectedId} onChange={selectRecord} required={field.required} disabled={loading || Boolean(error)} className={className}>
      <option value="">{loading ? 'Buscando registros...' : 'Selecione um registro...'}</option>
      {selectedId && !options.some(option => option.id === selectedId) && <option value={selectedId}>{savedLabel}</option>}
      {options.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
    </select>
    {selectedId && <button type="button" onClick={() => selectRecord({ target: { value: '' } })} className="text-sm text-blue-700 underline">Limpar seleção</button>}
    {error && <div role="alert" className="text-sm text-red-700">{error} <button type="button" className="underline" onClick={() => setRetry(n => n + 1)}>Tentar novamente</button></div>}
    {!loading && !error && options.length === 0 && <p className="text-xs text-slate-500">Nenhum registro encontrado.</p>}
    {hasMore && <p className="text-xs text-slate-500">Há mais registros. Refine a busca para encontrar o desejado.</p>}
    {!error && <p className="text-xs text-slate-500">Ao selecionar, os campos vinculados serão preenchidos. Trocar ou limpar a seleção substitui esses dados.</p>}
  </div>;
}
